import { useEffect } from 'react';
import { useAdiaStore } from '@/lib/store';
import { refreshPrayerEnrichment } from '@/lib/prayer-enrichment';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { useReconnectRetry } from './useReconnectRetry';
import { MAX_AUTOMATIC_ENRICHMENTS_PER_PASS } from '@/lib/privacy-safe-enrichment';

// App-wide durable-enrichment retry worker (offline-first Chunk 2, §3.4).
// Sibling to usePrayerEnrichment (the initial-parse worker); mounted next to
// it in App.tsx's PrayerEnrichmentMount. Iterates refreshPrayerEnrichment over
// every prayer that carries a pendingEnrichment token. Keyed on the
// id:updatedAt signature of that set so it re-runs when a token is added,
// resolved, or its row's updatedAt moves, AND on the shared reconnect/settle
// tick so an offline-failed or pre-pull-deferred refresh is re-attempted once
// back online / the pull settles / account state resolves.
//
// refreshPrayerEnrichment is itself single-flight per prayer and bails on no
// token / per-device defer / no content change, so a broad re-fire is safe and
// converges (new tokens come only from bounded user actions).
export function usePrayerEnrichmentRetry(): void {
  const signature = useAdiaStore((s) =>
    s.prayers
      .filter((p) => p.pendingEnrichment != null)
      .map((p) => `${p.id}:${p.updatedAt}`)
      .join(','),
  );
  const { tick } = useReconnectRetry();

  useEffect(() => {
    // Core-content cutover: no enrichment work runs at all (the tokens
    // stay durable on their rows for a later writes-enabled release).
    if (isCoreReadOnly()) return;
    const targets = useAdiaStore
      .getState()
      .prayers
      .filter((p) => p.pendingEnrichment != null)
      .slice(0, MAX_AUTOMATIC_ENRICHMENTS_PER_PASS);
    let cancelled = false;
    void (async () => {
      for (const prayer of targets) {
        if (cancelled) break;
        try {
          await refreshPrayerEnrichment(prayer.id);
        } catch {
          break;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [signature, tick]);
}
